import { OrderDirection } from "common/contracts/order-direction";
import PaginationParameters from "common/contracts/pagination-parameters";
import Guid from "common/values/guid/guid";
import { BoxType } from "notifications/values/box-type/box-type";

export interface INotificationFilters {
    query?: string,
    boxType?: BoxType,
    type?: string;
    from?: string;
    to?: string;
    description?: string;
    minCreatedDate?: Date;
    maxCreatedDate?: Date;
    isUnread?: boolean;
    isActionRequired?: boolean;
    isMessageNotification?: boolean;
    topicId?: Guid;
}

export class NotificationFilterParameters {
    query?: string;
    type?: string;
    from?: string;
    to?: string;
    description?: string;
    minCreatedDate?: Date;
    maxCreatedDate?: Date;
    isUnread?: boolean;
    boxType?: BoxType;
    isActionRequired?: boolean;
    isMessageNotification?: boolean;
    topicId?: Guid;

    constructor(
        filters?: INotificationFilters
    ) {
        this.query = filters?.query;
        this.type = filters?.type;
        this.from = filters?.from;
        this.to = filters?.to;
        this.description = filters?.description;
        this.minCreatedDate = filters?.minCreatedDate ? new Date(filters.minCreatedDate) : undefined;
        this.maxCreatedDate = filters?.maxCreatedDate ? new Date(filters.maxCreatedDate) : undefined;
        this.isUnread = filters?.isUnread;
        this.boxType = filters?.boxType;
        this.isActionRequired = filters?.isActionRequired;
        this.isMessageNotification = filters?.isMessageNotification;
        this.topicId = filters?.topicId;
    }

    public asSearchParams(): URLSearchParams {
        const searchParams = new URLSearchParams();
        if (this.query) searchParams.append("query", this.query);
        if (this.type) searchParams.append("type", this.type);
        if (this.from) searchParams.append("from", this.from);
        if (this.to) searchParams.append("to", this.to);
        if (this.description) searchParams.append("description", this.description);
        if (this.minCreatedDate) searchParams.append("minCreatedDate", this.minCreatedDate.toISOString());
        if (this.maxCreatedDate) searchParams.append("maxCreatedDate", this.maxCreatedDate.toISOString());
        if (this.isUnread !== undefined) searchParams.append("isUnread", String(this.isUnread));
        if (this.boxType) searchParams.append("boxType", this.boxType);
        if (this.isActionRequired !== undefined) searchParams.append("isActionRequired", String(this.isActionRequired));
        if (this.isMessageNotification !== undefined) {
            searchParams.append("isMessageNotification", String(this.isMessageNotification));
        }
        if (this.topicId) searchParams.append("topicId", this.topicId.toString());
        return searchParams;
    }

    public isEqualTo(other: NotificationFilterParameters): boolean {
        if (!other) return false;
        return this.query === other.query &&
            this.type === other.type &&
            this.from === other.from &&
            this.to === other.to &&
            this.description === other.description &&
            this.minCreatedDate?.getTime() === other.minCreatedDate?.getTime() &&
            this.maxCreatedDate?.getTime() === other.maxCreatedDate?.getTime() &&
            this.isUnread === other.isUnread &&
            this.boxType === other.boxType &&
            this.isActionRequired === other.isActionRequired &&
            this.isMessageNotification === other.isMessageNotification &&
            this.topicId?.toString() === other.topicId?.toString();
    }
}

export enum NotificationOrderField {
    From = "From",
    To = "To",
    CreatedDate = "CreatedDate",
}

export class NotificationOrderParameters {
    orderBy?: NotificationOrderField;
    orderDir?: OrderDirection;

    constructor(field: NotificationOrderField, direction: OrderDirection) {
        this.orderBy = field;
        this.orderDir = direction;
    }

    public asSearchParams(): URLSearchParams {
        const searchParams = new URLSearchParams();
        if (this.orderBy) searchParams.append("orderBy", this.orderBy);
        if (this.orderDir) searchParams.append("orderDir", this.orderDir);
        return searchParams;
    }

    public isEqualTo(other: NotificationOrderParameters): boolean {
        if (!other) return false;
        return this.orderBy === other.orderBy &&
            this.orderDir === other.orderDir;
    }
}

export default class NotificationQuery {
    filter: NotificationFilterParameters;
    order: NotificationOrderParameters;
    pagination: PaginationParameters;

    constructor(
        filter: NotificationFilterParameters,
        order: NotificationOrderParameters,
        pagination: PaginationParameters
    ) {
        this.filter = filter;
        this.order = order;
        this.pagination = pagination;
    }

    public asSearchParams(): URLSearchParams {
        return new URLSearchParams([
            ...this.pagination.asSearchParams(),
            ...this.order.asSearchParams(),
            ...this.filter.asSearchParams()
        ]);
    }

    public isEqualTo(other: NotificationQuery): boolean {
        if (!other) return false;
        return this.filter.isEqualTo(other.filter) &&
            this.order.isEqualTo(other.order) &&
            this.pagination.isEqualTo(other.pagination);
    }
}